/**
 * Question World Module - API Configuration
 * Self-contained axios instance and API functions
 */

import axios from 'axios';
import toastr from 'toastr';
import { base_url } from './constants';

// Create axios instance with base configuration
const sessionId = localStorage.getItem('sessionId');

export const instance = axios.create({
    baseURL: base_url,
    headers: {
        agent: 'browser',
        Authorization: sessionId
    },
    timeout: 1000 * 60,
    withCredentials: false
});

// Response interceptor for error handling
instance.interceptors.response.use(
    (response) => response,
    (error) => {
        console.log(error.message, JSON.stringify(error));
        if (
            error?.response?.status === 401 &&
            error?.response?.data?.errors?.code === 'notLoggedIn'
        ) {
            toastr.error(error?.response?.data?.errors?.title);
            localStorage.clear();
            window.location.replace('/');
        }
        return Promise.reject(error);
    }
);

/**
 * Fetch all instant practice exams for a student
 * @returns {Promise<Array>} - Array of exam objects with groupId and groupName
 */
export const fetchInstantPracticeExams = async () => {
    try {
        const response = await instance.get('/exam/instant-practice');
        return response?.data?.data || [];
    } catch (error) {
        console.error('Error fetching instant practice exams:', error);
        throw error;
    }
};

/**
 * Fetch exam details with questions
 * @param {string} examId - Exam ID
 * @param {string} groupId - Group ID
 * @returns {Promise<Object>} - Exam object with questions
 */
export const fetchExamById = async (examId, groupId) => {
    try {
        const response = await instance.get(`/exam/id/${examId}/groupId/${groupId}`);
        return response?.data?.data || null;
    } catch (error) {
        console.error('Error fetching exam:', error);
        throw error;
    }
};

/**
 * Fetch bookmarked questions for a student
 * @param {string} studentId - Student ID
 * @returns {Promise<Array>} - Array of bookmarked question IDs
 */
export const fetchBookmarkedQuestions = async (studentId) => {
    try {
        const response = await instance.get('/student-question-bookmark', {
            params: { student_id: studentId }
        });
        return response?.data?.data || [];
    } catch (error) {
        console.error('Error fetching bookmarks:', error);
        throw error;
    }
};

/**
 * Add bookmark to a question
 * @param {string} studentId - Student ID
 * @param {string} questionId - Question ID
 * @param {string} examId - Exam ID
 * @returns {Promise<Object>} - Bookmark response
 */
export const addBookmark = async (studentId, questionId, examId) => {
    try {
        const response = await instance.post('/student-question-bookmark', {
            student_id: studentId,
            question_id: questionId,
            exam_id: examId,
            date: new Date().toISOString()
        });
        return response?.data?.data;
    } catch (error) {
        console.error('Error adding bookmark:', error);
        throw error;
    }
};

/**
 * Remove bookmark from a question
 * @param {string} bookmarkId - Bookmark ID
 * @returns {Promise<Object>} - Delete response
 */
export const removeBookmark = async (bookmarkId) => {
    try {
        await instance.delete(`/student-question-bookmark/${bookmarkId}`);
    } catch (error) {
        console.error('Error removing bookmark:', error);
        throw error;
    }
};

/**
 * Fetch all groups for a student (to get exam list)
 * @param {string} groupId - Group ID
 * @returns {Promise<Object>} - Group data with exams
 */
export const fetchGroupById = async (groupId) => {
    try {
        const response = await instance.get(`/group/${groupId}`);
        return response?.data?.data || null;
    } catch (error) {
        console.error('Error fetching group:', error);
        throw error;
    }
};

/**
 * Fetch student profile
 * @returns {Promise<Object>} - Student profile data
 */
export const fetchStudentProfile = async () => {
    try {
        const response = await instance.get('/student/profile');
        return response?.data?.data || null;
    } catch (error) {
        console.error('Error fetching student profile:', error);
        throw error;
    }
};

/**
 * Gets signed URL for file upload
 * @param {string} fileType - The file MIME type
 * @returns {Promise<object>} A promise that resolves to the signed URL data
 */
export const getSignedUrl = async (fileType) => {
    try {
        const response = await instance.post('/file/signed-url', { type: fileType });
        return response.data.data;
    } catch (error) {
        console.error('Error getting signed URL:', error);
        throw error;
    }
};

/**
 * Uploads file to signed URL
 * @param {string} signedUrl - The signed URL
 * @param {File} file - The file to upload
 * @returns {Promise<void>}
 */
export const uploadFile = async (signedUrl, file) => {
    try {
        await axios.put(signedUrl, file, {
            headers: { 'Content-Type': file.type }
        });
    } catch (error) {
        console.error('Error uploading file:', error);
        throw error;
    }
};
